import React, { useState, useRef, useEffect } from "react";
import { connect } from "react-redux";
import { useNavigate, useParams, useLocation } from "react-router-dom";
import { getDataFromAPI } from "../../../HandleAPICalls/actions";
import { toastr } from "react-redux-toastr";
import Sidebar from "../../Navigation/Sidebar/Sidebar";
import NavigationalUpdateComponent from "./component";

const NavigationalUpdateContainer = (props) => {
  const navigate = useNavigate();
  const { id } = useParams();
  const location = useLocation();
  
  const stateData = location.state?.navigationalData;
  const sourceTab = location.state?.sourceTab || "category";
  
  const [isLoading, setIsLoading] = useState(true);
  const [isLocked, setIsLocked] = useState(true);
  const [categoryData, setCategoryData] = useState(null);
  const [showSuccessPopup, setShowSuccessPopup] = useState(false);
  const [updatedItemName, setUpdatedItemName] = useState("");
  const [updatedItemId, setUpdatedItemId] = useState("");
  
  const [formData, setFormData] = useState({
    name: "",
    description: "",
    active: true,
  });

  const [iconFile, setIconFile] = useState(null);
  const [bgFile, setBgFile] = useState(null);
  const [iconPreview, setIconPreview] = useState(null);
  const [bgPreview, setBgPreview] = useState(null);
  const [useTransparentBg, setUseTransparentBg] = useState(false);
  const [variantIds, setVariantIds] = useState([]);
  const [inputValue, setInputValue] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isIconUploading, setIsIconUploading] = useState(false);
  const [isBgUploading, setIsBgUploading] = useState(false);
  const [iconPath, setIconPath] = useState("");
  const [bgPath, setBgPath] = useState("");
  const [isVerifying, setIsVerifying] = useState(false);
  const [verifiedVariants, setVerifiedVariants] = useState([]);
  const [variantDetails, setVariantDetails] = useState({});

  const iconInputRef = useRef(null);
  const backgroundInputRef = useRef(null);

  // REMOVED isFormValid validation - button always enabled
  // User can update single field even after removing images

  // S3 Base URL
  const S3_BASE_URL = "https://s3.ap-south-1.amazonaws.com/daalchini/dcprod/";

  const getFullImageUrl = (imagePath) => {
    if (!imagePath) return "";
    if (imagePath.startsWith("http")) return imagePath;
    return `${S3_BASE_URL}${imagePath}`;
  };

  const getPathFromUrl = (url) => {
    if (!url) return "";
    if (url.includes("daalchini/dcprod/")) {
      return url.split("daalchini/dcprod/")[1];
    }
    return url;
  };

  // Auto Upload Icon
  const uploadIcon = async (file) => {
    if (!file) return;
    setIsIconUploading(true);
    const formDataUpload = new FormData();
    formDataUpload.append("file", file);

    return new Promise((resolve, reject) => {
      props.getDataFromAPI(
        "/partner/api/v2/s3upload/machine-image",
        "POST",
        formDataUpload,
        (response) => {
          setIsIconUploading(false);
          const uploadedPath = response.image_path || response.image_path;
          const fullUrl = getFullImageUrl(uploadedPath);
          setIconPath(fullUrl);
          toastr.success("Success", "Icon uploaded successfully");
          resolve(uploadedPath);
        },
        (err) => {
          setIsIconUploading(false);
          toastr.error("Failed", "Unable to upload icon");
          reject(err);
        }
      );
    });
  };

  // Auto Upload Background
  const uploadBackground = async (file) => {
    if (!file) return;
    setIsBgUploading(true);
    const formDataUpload = new FormData();
    formDataUpload.append("file", file);

    return new Promise((resolve, reject) => {
      props.getDataFromAPI(
        "/partner/api/v2/s3upload/machine-image",
        "POST",
        formDataUpload,
        (response) => {
          setIsBgUploading(false);
          const uploadedPath = response.image_path || response?.image_path;
          const fullUrl = getFullImageUrl(uploadedPath);
          setBgPath(fullUrl);
          toastr.success("Success", "Background uploaded successfully");
          resolve(uploadedPath);
        },
        (err) => {
          setIsBgUploading(false);
          toastr.error("Failed", "Unable to upload background");
          reject(err);
        }
      );
    });
  };

  // Handle Icon Selection - Auto Upload
  const handleIconFileSelect = async (e) => {
    if (isLocked) return;
    const file = e.target.files?.[0];
    if (file) {
      setIconFile(file);
      const reader = new FileReader();
      reader.onloadend = () => setIconPreview(reader.result);
      reader.readAsDataURL(file);
      await uploadIcon(file);
    }
  };

  // Handle Background Selection - Auto Upload
  const handleBgFileSelect = async (e) => {
    if (isLocked) return;
    const file = e.target.files?.[0];
    if (file) {
      setBgFile(file);
      const reader = new FileReader();
      reader.onloadend = () => setBgPreview(reader.result);
      reader.readAsDataURL(file);
      await uploadBackground(file);
    }
  };

  const handleClearIcon = () => {
    setIconFile(null);
    setIconPreview(null);
    setIconPath("");
    if (iconInputRef.current) iconInputRef.current.value = "";
    // Button remains enabled - user can save without icon
  };

  const handleClearBg = () => {
    setBgFile(null);
    setBgPreview(null);
    setBgPath("");
    if (backgroundInputRef.current) backgroundInputRef.current.value = "";
    // Button remains enabled - user can save without background
  };

  const handleToggleTransparent = () => {
    if (isLocked) return;
    const newVal = !useTransparentBg;
    setUseTransparentBg(newVal);
    if (newVal) {
      setBgPath("");
      setBgPreview(null);
      setBgFile(null);
    }
  };

  // Check Variant IDs with details
  const handleCheckVariants = async (idsToCheck) => {
    setIsVerifying(true);
    const requestBody = idsToCheck.map(id => ({ variantId: parseInt(id) || id }));
    
    return new Promise((resolve, reject) => {
      props.getDataFromAPI(
        "/partner/api/v6/product/variant/check",
        "POST",
        requestBody,
        (response) => {
          setIsVerifying(false);
          const data = response || response || [];
          const validVariantIds = [];
          const invalidVariantIds = [];
          const detailsMap = {};
          
          data.forEach(item => {
            const variantId = item.variantId;
            if (item.status === true) {
              validVariantIds.push(variantId);
              detailsMap[variantId] = {
                id: variantId,
                name: item.name || `Variant ${variantId}`,
                image: item.image || null,
                status: item.status
              };
            } else {
              invalidVariantIds.push(variantId);
            }
          });
          
          resolve({ validVariantIds, invalidVariantIds, detailsMap });
        },
        (err) => {
          setIsVerifying(false);
          toastr.error("Verification Failed", "Unable to verify variant IDs");
          reject(err);
        }
      );
    });
  };

  const handleAddId = async () => {
    if (isLocked) return;
    if (inputValue.trim()) {
      const ids = inputValue.split(/[\s,]+/).filter(id => id.trim() && !verifiedVariants.includes(parseInt(id)));
      
      if (ids.length > 0) {
        try {
          const { validVariantIds, invalidVariantIds, detailsMap } = await handleCheckVariants(ids);
          
          if (invalidVariantIds.length > 0) {
            toastr.warning("Invalid Variants", `${invalidVariantIds.length} variant(s) are invalid: ${invalidVariantIds.join(", ")}`);
          }
          
          if (validVariantIds.length > 0) {
            const newValidIds = validVariantIds.filter(id => !verifiedVariants.includes(id));
            const newDetails = { ...variantDetails };
            newValidIds.forEach(id => {
              if (detailsMap[id]) newDetails[id] = detailsMap[id];
            });
            setVariantDetails(newDetails);
            setVerifiedVariants([...verifiedVariants, ...newValidIds]);
            toastr.success("Success", `${newValidIds.length} variant(s) verified successfully`);
          }
          setInputValue("");
        } catch (error) {
          console.error("Error verifying variants:", error);
        }
      }
      setInputValue("");
    }
  };

  const removeId = (idToRemove) => {
    if (isLocked) return;
    setVerifiedVariants(verifiedVariants.filter(id => id !== idToRemove));
    const newDetails = { ...variantDetails };
    delete newDetails[idToRemove];
    setVariantDetails(newDetails);
  };

  const handleClearAllVariants = () => {
    if (isLocked) return;
    setVerifiedVariants([]);
    setVariantDetails({});
    toastr.info("Cleared", "All variants cleared");
  };

  // Fetch navigational details
  const fetchNavigationalDetails = () => {
    setIsLoading(true);
    
    props.getDataFromAPI(
      `/partner/api/v1/nav-categories/${id}`,
      "GET",
      undefined,
      (response) => {
        setIsLoading(false);
        let data = response?.data || response;
        
        if (data) {
          setCategoryData(data);
          setFormData({
            name: data.name || "",
            description: data.description || "",
            active: data.active === true,
          });
          
          const iconFullUrl = getFullImageUrl(data.imagePath);
          const bgFullUrl = getFullImageUrl(data.bgImagePath);
          
          setIconPath(iconFullUrl);
          setBgPath(bgFullUrl);
          setIconPreview(iconFullUrl);
          setBgPreview(bgFullUrl);
          
          // Handle variant details from API response
          if (data.variantDetails && data.variantDetails.length > 0) {
            const variants = data.variantDetails.map(v => v.id);
            setVerifiedVariants(variants);
            
            const detailsMap = {};
            data.variantDetails.forEach(v => {
              detailsMap[v.id] = {
                id: v.id,
                name: v.name || `Variant ${v.id}`,
                image: v.image || null
              };
            });
            setVariantDetails(detailsMap);
          } else if (data.variantIds && data.variantIds.length > 0) {
            setVerifiedVariants(data.variantIds);
          }
          
          setUseTransparentBg(!data.bgImagePath || data.bgImagePath === "");
        } else {
          toastr.error("Error", "No data found");
          navigate(-1);
        }
      },
      (error) => {
        setIsLoading(false);
        toastr.error("Error", error?.message || "Failed to fetch details");
        navigate(-1);
      }
    );
  };

  useEffect(() => {
    if (id) {
      if (stateData) {
        setFormData({
          name: stateData.name || "",
          description: stateData.description || "",
          active: stateData.status === "Active",
        });
        setIconPath(stateData.iconUrl || "");
        setBgPath(stateData.backgroundUrl || "");
        setIconPreview(stateData.iconUrl || null);
        setBgPreview(stateData.backgroundUrl || null);
        setVerifiedVariants(stateData.variantIds || []);
        setIsLoading(false);
      } else {
        fetchNavigationalDetails();
      }
    }
  }, [id]);

  const handleBackClick = () => navigate(-1);

  const handleSave = () => {
    // No validation - allow save even with empty fields
    setIsSubmitting(true);

    const payload = {
      name: formData.name || "",
      description: formData.description || "",
      imagePath: getPathFromUrl(iconPath) || "",
      bgImagePath: useTransparentBg ? "" : (getPathFromUrl(bgPath) || ""),
      bgTransparent: useTransparentBg,
      active: formData.active,
      variantIds: verifiedVariants.map(id => parseInt(id) || id),
    };

    console.log("Saving payload:", payload);

    props.getDataFromAPI(
      `/partner/api/v1/nav-categories/${id}`,
      "PUT",
      payload,
      (response) => {
        setIsSubmitting(false);
        setUpdatedItemName(formData.name || "Navigational");
        setUpdatedItemId(id);
        setShowSuccessPopup(true);
        toastr.success("Success", "Navigational updated successfully!");
      },
      (error) => {
        setIsSubmitting(false);
        toastr.error("Error", error?.message || "Failed to update navigational");
      }
    );
  };

  const handleCloseSuccessPopup = () => {
    setShowSuccessPopup(false);
    navigate(-1);
  };

  // Get category name for header
  const getCategoryName = () => {
    if (stateData?.name) return stateData.name;
    if (categoryData?.name) return categoryData.name;
    return "Update Navigational";
  };

  if (isLoading) {
    return (
      <div className="nav-update-container">
        <div className="nav-update-content">
          <div className="loading-container">
            <div className="spinner"></div>
            <p>Loading navigational details...</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <NavigationalUpdateComponent
      formData={formData}
      setFormData={setFormData}
      isLocked={isLocked}
      setIsLocked={setIsLocked}
      iconPreview={iconPreview}
      bgPreview={bgPreview}
      useTransparentBg={useTransparentBg}
      variantIds={verifiedVariants}
      variantDetails={variantDetails}
      inputValue={inputValue}
      setInputValue={setInputValue}
      iconInputRef={iconInputRef}
      backgroundInputRef={backgroundInputRef}
      isSubmitting={isSubmitting}
      isIconUploading={isIconUploading}
      isBgUploading={isBgUploading}
      isVerifying={isVerifying}
      handleToggleTransparent={handleToggleTransparent}
      handleAddId={handleAddId}
      removeId={removeId}
      handleClearAllVariants={handleClearAllVariants}
      handleBackClick={handleBackClick}
      handleSave={handleSave}
      handleIconFileSelect={handleIconFileSelect}
      handleBgFileSelect={handleBgFileSelect}
      handleClearIcon={handleClearIcon}
      handleClearBg={handleClearBg}
      iconPath={iconPath}
      bgPath={bgPath}
      sourceTab={sourceTab}
      categoryName={getCategoryName()}
      showSuccessPopup={showSuccessPopup}
      onSuccessClose={handleCloseSuccessPopup}
      updatedItemName={updatedItemName}
      updatedItemId={updatedItemId}
    />
  );
};

function mapStateToProps(state) {
  return { props: state };
}

export default connect(mapStateToProps, { getDataFromAPI })(NavigationalUpdateContainer);